import { useState } from 'react';

import type { FormAssignmentGroup } from '../../types';
import { SectionCard } from './SectionCard';

const SYS_ID_RE = /^[0-9a-f]{32}$/i;

/**
 * Assignment-group section. Mirrors the original standalone app:
 * team name, manager, the routing rule that selected it, member count,
 * and a "Show members" toggle that defaults closed (rosters can run to
 * 20+ users; the section stays scannable until the user asks for it).
 */
export function AssignmentGroup({ group }: { group: FormAssignmentGroup | null }) {
  const [showMembers, setShowMembers] = useState(false);

  if (!group) {
    return (
      <SectionCard title="Assignment Group (example)" defaultOpen={false}>
        <p className="text-sm text-slate-500 italic">
          Not configured on this form.
        </p>
      </SectionCard>
    );
  }

  const displayName = looksLikeSysId(group.name) ? null : group.name;
  const manager = looksLikeSysId(group.manager) ? null : group.manager;
  const memberCount = group.members.length;
  const ticketLabel =
    group.ticket_count != null
      ? `${group.ticket_count.toLocaleString()} ticket${group.ticket_count === 1 ? '' : 's'}`
      : null;
  const memberLabel = `${memberCount} member${memberCount === 1 ? '' : 's'}`;

  return (
    <SectionCard
      title="Assignment Group (example)"
      defaultOpen
      rightSlot={
        memberCount > 0 ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowMembers((v) => !v);
            }}
            className="text-xs text-brand-danone hover:underline"
          >
            {showMembers ? 'Hide members' : 'Show members'}
          </button>
        ) : null
      }
    >
      <div className="space-y-1">
        <div className="text-base font-semibold text-slate-900">
          {displayName || 'Team'}
        </div>
        {manager && (
          <div className="text-sm text-slate-600">
            <span className="text-slate-500">Manager:</span> {manager}
          </div>
        )}
        {group.assignment_rule && (
          <div className="text-sm text-slate-600 italic">
            <span className="text-slate-500 not-italic">Assignment rule:</span>{' '}
            {group.assignment_rule}
          </div>
        )}
        <div className="text-xs text-slate-500">
          {[memberLabel, ticketLabel].filter(Boolean).join(' · ')}
          {group.email && (
            <>
              {' · '}
              <a
                href={`mailto:${group.email}`}
                className="text-brand-danone hover:underline"
              >
                {group.email}
              </a>
            </>
          )}
        </div>
      </div>

      {showMembers && memberCount > 0 && (
        <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5 text-sm">
          {group.members.map((m) => (
            <li
              key={m.sys_id}
              className="flex items-baseline gap-2 min-w-0 text-slate-800"
            >
              <span className="truncate">{m.name || m.user_name || '—'}</span>
              {m.email && (
                <a
                  href={`mailto:${m.email}`}
                  className="text-[11px] text-slate-400 hover:text-brand-danone truncate"
                >
                  {m.email}
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function looksLikeSysId(s: string | null | undefined): boolean {
  return !!s && SYS_ID_RE.test(s);
}
